import { useRef } from 'react';

import { useAppDispatch } from '../../hooks';
import AppModal from '../../ui-components/AppModal';
import { formatTitle } from '../../utils/common';

import { useDynamicMenuListQuery } from './dynamicMenuApi';
import { setEditingId } from './dynamicMenuSlice';
import { ManageMenu } from './ManageMenu';
import { useMenuActions } from './MenuAction';
import { MenuItemComponent } from './MenuItemComponent';

import useAutoScroll from './useAutoScroll';

import { useMenuDragDrop } from './useMenuDragDrop';

import type { IAllMenu } from './model';

export const MenuDragDrop = () => {
  const dispatch = useAppDispatch();
  const { data: apiResponse } = useDynamicMenuListQuery();

  const menuData: IAllMenu[] = apiResponse?.data?.records || [];

  const { allMenu, handleDrop, handleSubMenuDrop } = useMenuDragDrop(menuData);

  const {
    editingId,
    deleteConfirmation,
    handleEditMenu,
    handleSaveMenu,
    openDeleteConfirmation,
    closeDeleteConfirmation,
    handleDeleteMenu,
  } = useMenuActions(allMenu);

  const findMenuById = (
    menus: IAllMenu[],
    id: number | null
  ): IAllMenu | null => {
    if (!menus || !id) {
      return null;
    }

    const findMenu = (menuList: IAllMenu[]): IAllMenu | null => {
      for (const menu of menuList) {
        if (menu.adminMenuId === id) {
          return menu;
        }
        if (menu.subMenu?.length) {
          const found = findMenu(menu.subMenu);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    return findMenu(menus);
  };

  const currentEditingMenu = editingId
    ? findMenuById(allMenu, editingId)
    : null;

  const handleSaveMenuLocal = (menus: IAllMenu) => {
    handleSaveMenu(menus);
  };

  const handleCancelEdit = () => {
    dispatch(setEditingId(null));
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  useAutoScroll(scrollRef);

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white shadow p-6" style={{ maxHeight: '30rem' }}>
          <div className="flex justify-between items-center mb-1 ">
            <h2 className="text-xl font-semibold text-gray-800">
              Dynamic Menu
            </h2>
          </div>
          <div
            className="overflow-y-auto"
            style={{ maxHeight: '26rem' }}
            ref={scrollRef}
          >
            <div className="space-y-1">
              {allMenu.map((item, index) => (
                <MenuItemComponent
                  key={item.adminMenuId}
                  item={item}
                  index={index}
                  onDrop={handleDrop}
                  onEdit={handleEditMenu}
                  onDelete={(id, name) => openDeleteConfirmation({ id, name })}
                  onSubMenuDrop={handleSubMenuDrop}
                />
              ))}
            </div>
          </div>
        </div>

        <div
          className="bg-white shadow p-6 overflow-y-auto"
          style={{ maxHeight: '30rem' }}
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Manage Menu
          </h2>

          <div className="space-y-4">
            <ManageMenu
              onSave={handleSaveMenuLocal}
              initialData={currentEditingMenu}
              onCancel={handleCancelEdit}
            />
          </div>
        </div>
      </div>

      <AppModal
        isOpen={deleteConfirmation.open}
        onClose={closeDeleteConfirmation}
        title="Confirm Delete"
        description={`Are you sure you want to delete "${formatTitle(deleteConfirmation.name)}"?`}
        maxWidth="max-w-md"
        actions={[
          {
            label: 'Cancel',
            variant: 'secondary',
            onClick: closeDeleteConfirmation,
          },
          {
            label: 'Delete',
            variant: 'primary',
            onClick: handleDeleteMenu,
          },
        ]}
      >
        <div />
      </AppModal>
    </div>
  );
};
